import React from "react";

import { Navigate, useRoutes } from "react-router";
import { useSearchParams } from "react-router-dom";

import { type RouteNames } from "src/client";
import ErrorBoundary from "src/components/common/ErrorBoundary";
import RecentlyVisitedRouteWrapper from "src/experiences/common/components/RecentlyVisitedRouteWrapper";

export interface RouteType {
  enabled?: boolean;
  path: string;
  element: React.ReactNode;
  routeName?: RouteNames;
}

interface RoutesProps {
  routes: RouteType[];
  rootUrl: string;
  defaultPath?: string;
  persistSearchParams?: boolean;
}

const Routes = ({
  routes,
  rootUrl,
  defaultPath,
  persistSearchParams = false,
}: RoutesProps): React.ReactElement | null => {
  const [searchParams] = useSearchParams();

  const availableRoutes = routes
    // routes are enabled by default. i.e. if undefined, assume available
    .filter((route) => route.enabled !== false)
    .map(({ path, element, routeName }) => ({
      path,
      element: (
        <ErrorBoundary key={path}>
          {routeName ? (
            <RecentlyVisitedRouteWrapper routeName={routeName}>
              {element}
            </RecentlyVisitedRouteWrapper>
          ) : (
            element
          )}
        </ErrorBoundary>
      ),
    }));

  const defaultRoute = (defaultPath ?? availableRoutes[0].path).replace(
    "/*",
    ""
  );

  const queryString =
    persistSearchParams && searchParams.toString()
      ? `?${searchParams.toString()}`
      : "";

  availableRoutes.push({
    path: "*",
    element: (
      <Navigate to={`${rootUrl}${defaultRoute}${queryString}`} replace />
    ),
  });

  return useRoutes(availableRoutes);
};

export default Routes;
